import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'
import * as ec from '@attain-apps/fe/web/main/lib/entity-collection'
import * as rxjs from 'rxjs'

import * as componentBridge from '../../../component-bridge'
import * as kbDocumentUploads from '../../kb-document-uploads'
import * as me from '../../me'
import * as entities from '../entities'
import * as schema from '../schema'
import * as snapshots from '../snapshots'

import { Api } from './api.service'
import * as effects from './effects'
import { createNewCreationForFiles } from './new-creation-for-files'
import type { Context, TypeParams } from './types'

@ngCore.Injectable()
export class Collection extends ec.Collection<TypeParams> {
  constructor() {
    const entitiesEc = ngCore.inject(entities.Collection)
    const meFeature = ngCore.inject(me.Feature)
    const snapshotsEc = ngCore.inject(snapshots.Collection)
    const kbDocumentUploadsFeature = ngCore.inject(kbDocumentUploads.Feature)
    const schemaFeature = ngCore.inject(schema.Feature)
    const referenceCellComponent = ngCore.inject(
      componentBridge.REFERENCE_CELL_COMPONENT,
    )
    super({
      api: ngCore.inject(Api),
      context$: rxjs
        .combineLatest({
          entityId:
            entitiesEc.plugins.currentEntityPlugin.feature.selectors.getId()
              .asObservable,
          snapshotId:
            snapshotsEc.plugins.currentEntityPlugin.feature.selectors.getId()
              .asObservable,
          zoneContext: meFeature.contexts.selectors.zoneContext.asObservable,
        })
        .pipe(
          rxjs.map(
            ({ zoneContext, entityId, snapshotId }): Context =>
              zoneContext
                ? {
                    ...zoneContext,
                    modelId: ents.core.dms.models.Entity.Id.isId(entityId)
                      ? entityId
                      : ents.core.dms.models.Snapshot.Id.isId(snapshotId)
                        ? snapshotId
                        : undefined,
                  }
                : undefined,
          ),
        ),
      identify: record => record.values[ents.core.dms.data.Record.Id.KEY],
      name: ec.Name.Predefined.DmsRecord,
      plugins: {
        additionalFactories: [
          new ds.table.ec.Factory<TypeParams>(collection => ({
            cellConfig: context =>
              [
                ents.core.dms.data.Value.Reference.Default.TYPE,
                ents.core.dms.data.Value.Reference.Multi.Default.TYPE,
              ].some(type => type === context.attribute.valueInfo?.type)
                ? {
                    component: referenceCellComponent,
                    inputs: { value: context.value },
                  }
                : undefined,
            rowConfig: ({ rowIdx, entity }) => ({
              iconsConfigs: [
                {
                  class: 'atn-icon atn-edit atn-blue',
                  onClick: () => {
                    const source = `Table.Row-${rowIdx}.Buttons.Edit.Clicked`
                    // Check if it already exists
                    if (
                      collection.plugins.currentEntityPlugin.feature.selectors
                        .getEditedEntity()
                        .asSignal()
                    ) {
                      // discard the current edit
                      collection.plugins.editingPlugin.feature.actions.discard
                        .from(source)
                        .dispatch({
                          current: true,
                        })
                    }

                    // start a new edit
                    collection.plugins.editingPlugin.feature.actions.edit
                      .from(source)
                      .dispatch({
                        id: entity.values[ents.core.dms.data.Record.Id.KEY],
                      })
                  },
                },
                {
                  class: 'atn-icon atn-trash atn-red',
                  onClick: () => {
                    collection.plugins.editingPlugin.feature.actions.markForDeletion
                      .from(`Table.Row-${rowIdx}.Icons.Trash.Clicked`)
                      .dispatch({
                        id: entity.values[ents.core.dms.data.Record.Id.KEY],
                      })
                  },
                },
              ],
            }),
          })),
        ],
        currentList: {
          quantity: {
            get: quantity => quantity ?? 20,
          },
        },
        editing: {
          newCreation: createNewCreationForFiles(
            entitiesEc,
            snapshotsEc,
            schemaFeature,
          ),
        },
      },
    })

    this.feature.registerEffects(
      effects.uploadPendingFilesOnSave(
        this,
        kbDocumentUploadsFeature,
        schemaFeature,
        meFeature,
      ),
    )
  }
}
